import { View, Platform, Pressable, StyleProp, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Tag, Text } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { UserAvatar } from '%components';
import CommentIcon from '%assets/icons/comment.svg';
import { PostCommentsList } from '%screens/posts/view/components/PostCommentsList';
import { MAX_DISPLAYED_COMMENTS_COUNT } from '%constants/Post';
import { PostType } from '%__generated__/types';
import { Heart as HeartIcon } from '../../icons/Heart';

import { PostMenu } from './PostMenu';
import PinIcon from './pin.svg';
import { PostData } from './types';
import { usePost } from './usePost';
import { useStyles } from './styles';
import { PostContent } from './content';
import { useInViewport } from './useInViewport';
import { PostCareRecipients } from './PostCareRecipients/PostCareRecipients';

interface Props {
	post: PostData;
	allowPin?: boolean;
	disableCommentsPress?: boolean;
	withComments?: boolean;
	withBackground?: boolean;
	style?: StyleProp<ViewStyle>;
}

const buttonIncreasedHitSlop = { bottom: 20, top: 20, left: 10, right: 15 };

export const Post = ({
	post,
	style,
	allowPin,
	disableCommentsPress,
	withComments,
	withBackground,
}: Props) => {
	const { t } = useTranslation();
	const styles = useStyles();
	const { isInView, ref, onLayout } = useInViewport({ checkIntervalInMs: 750, offset: 100 });
	const {
		isLiked,
		disabledLike,
		likesCount,
		toggleLikePost,
		publicationTime,
		onAllCommentsClick,
		onLikesClick,
		onAvatarPress,
	} = usePost(post, isInView);
	const confirmed = !post.id.includes('temp-id');
	const media = post.mediaFiles || [];

	return (
		<View
			style={[
				styles.root,
				confirmed ? undefined : styles.notConfirmed,
				withBackground && styles.withBackground,
				post.type === PostType.Organisation && styles.fromOrganisation,
				style,
			]}
			ref={ref}
			onLayout={onLayout}
		>
			<View style={styles.header}>
				<View style={[styles.flexRowCenter, styles.flexShrink]}>
					<Pressable style={styles.avatar} onPress={onAvatarPress}>
						<UserAvatar
							size={Platform.OS === 'web' ? 48 : 40}
							username={getName(post.author)}
							src={post.author?.photoUrl}
						/>
					</Pressable>
					<View style={styles.flexShrink}>
						<View style={styles.users}>
							<Text
								category={Platform.OS === 'web' ? 'h4' : 'h6'}
								numberOfLines={2}
								style={styles.username}
							>
								{getName(post.author)}
							</Text>
						</View>
						<View style={styles.users}>
							{post.selectedUsers && (
								<PostCareRecipients
									users={post.selectedUsers.map(({ user }) => ({
										...user,
										profilePhoto: user.photoUrl ? { url: user.photoUrl } : undefined,
									}))}
								/>
							)}
						</View>
						<View style={styles.flexRowCenter}>
							<Text category={Platform.OS === 'web' ? 'p1' : 's2'} appearance="hint">
								{publicationTime}
							</Text>
							{allowPin && post.pinned && (
								<>
									<PinIcon style={styles.pinnedIcon} />
									<Text category="s2" appearance="hint">
										{t('post.pinned-post')}
									</Text>
								</>
							)}
						</View>
					</View>
				</View>

				<View style={styles.menusRight}>
					{post.type === PostType.Organisation && <Tag text={t('post.admin')} />}

					<PostMenu
						post={post}
						allowPin={allowPin}
						disabled={!confirmed}
						onFeed={!disableCommentsPress}
					/>
				</View>
			</View>

			<PostContent content={post.content} mediaFiles={media} type={post.type} />

			<View style={styles.actions}>
				<View style={styles.reactionContainer}>
					<Pressable
						onPress={toggleLikePost}
						disabled={!confirmed || disabledLike}
						hitSlop={buttonIncreasedHitSlop}
					>
						<HeartIcon active={isLiked} />
					</Pressable>
					<Pressable
						onPress={onLikesClick}
						disabled={likesCount === 0 || !confirmed || disabledLike}
					>
						<Text
							appearance="hint"
							style={styles.reactionCount}
							status={Platform.OS === 'web' ? 'primary' : 'basic'}
						>
							{likesCount}
						</Text>
					</Pressable>
				</View>

				<Pressable
					style={({ pressed }) => [styles.reactionContainer, pressed && styles.pressed]}
					onPress={onAllCommentsClick}
					disabled={!confirmed || disableCommentsPress}
					hitSlop={buttonIncreasedHitSlop}
				>
					<CommentIcon />
					<Text
						appearance="hint"
						style={styles.reactionCount}
						status={Platform.OS === 'web' ? 'primary' : 'basic'}
					>
						{post.comments.length}
					</Text>
				</Pressable>
			</View>

			{withComments && (
				<PostCommentsList
					commentStyle={styles.commentStyle}
					maxComments={MAX_DISPLAYED_COMMENTS_COUNT}
					postId={post.id}
					comments={post.comments}
				/>
			)}
		</View>
	);
};
